import { Card, CardContent, CardHeader, Typography } from "@mui/material";
import { BooleanInput, NumberInput, SelectInput, TextInput, required } from "react-admin";
import { FieldValues } from "react-hook-form";
import CustomDateTimeInput from "shared/components/inputs/CustomDateTimeInput";
import { PlanningModeEnum } from "shared/enums/globalEnums";
import { chronicleFilenamesChoices } from "shared/enums/reactAdminChoices";

import { usePlanningChain } from "./hooks/usePlanningChain";
import { GenericPlanningConfig } from "./types";

type Props = {
  allConfigs: GenericPlanningConfig[];
  config: GenericPlanningConfig;
  index: number;
  initialData: FieldValues;
  lockSocEndDate?: boolean; // default true
};

export default function FingridTestGenericPlanningItem({
  allConfigs,
  config,
  index,
  initialData,
  lockSocEndDate = true,
}: Props) {
  const {
    currentActive,
    isOverlappingDays,
    minDateTime,
    validateStartDate,
    handleStartDateChange,
    handleEndDateChange,
    handleActiveToggle,
  } = usePlanningChain({
    allConfigs,
    config,
    index,
    initialStartDate: initialData.startDate,
  });

  const isSoc = config.mode === PlanningModeEnum.soc_management;
  const isFcrN = config.mode === PlanningModeEnum.fingrid_fcrn;
  const isFcrD = config.mode === PlanningModeEnum.fingrid_fcrd;
  const isAfrr = config.mode === PlanningModeEnum.fingrid_afrr;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 2,
        width: "100%",
        backgroundColor: !currentActive ? "rgba(128,128,128,0.3)" : isOverlappingDays ? "rgba(255,0,0,0.2)" : "inherit",
      }}
    >
      <CardHeader
        title={config.name}
        subheader={`Default duration: ${config.durationInMinutes} minutes`}
        action={
          <BooleanInput
            source={`${config.name}.active`}
            label="Active"
            defaultValue={true}
            onChange={(e) => handleActiveToggle(e.target.checked)}
          />
        }
      />
      <CardContent>
        <CustomDateTimeInput
          source={`${config.name}.start_date`}
          label="Start Date"
          onChange={handleStartDateChange}
          validate={validateStartDate}
          minDateTime={minDateTime}
          disabled={!currentActive}
        />

        <CustomDateTimeInput
          source={`${config.name}.end_date`}
          label="End Date"
          onChange={handleEndDateChange}
          disabled={!currentActive}
          readOnly={lockSocEndDate && isSoc}
          sx={
            lockSocEndDate && isSoc
              ? { "& .MuiInputBase-input": { bgcolor: "rgba(0, 0, 0, 0.08)", color: "GrayText" } }
              : {}
          }
        />

        <TextInput source={`${config.name}.mode`} label="Mode" readOnly />

        {/* SOC */}
        {isSoc && <NumberInput source={`${config.name}.targetSoc`} label="Target SOC" disabled={!currentActive} />}

        {/* FCR-N */}
        {isFcrN && (
          <>
            <NumberInput source={`${config.name}.fcrn_engagement`} label="fcr N engagement" />
            {config.chronicleFilename && (
              <SelectInput
                source={`${config.name}.chronicleFilename`}
                label="Chronicle Filename"
                choices={chronicleFilenamesChoices}
                validate={required()}
                disabled={!currentActive}
              />
            )}
          </>
        )}

        {/* FCR-D (hidden by default; constants are injected via defaults & mapper) */}
        {isFcrD && (
          <>
            <NumberInput source={`${config.name}.fcrd_up_engagement`} label="FCR-D Up" />
            <NumberInput source={`${config.name}.fcrd_down_engagement`} label="FCR-D Down" />
            {config.chronicleFilename && (
              <TextInput source={`${config.name}.chronicleFilename`} label="Chronicle Filename" />
            )}
          </>
        )}
        {isAfrr && (
          <>
            <NumberInput source={`${config.name}.afrr_engagement_up`} label="AFRR Up" />
            <NumberInput source={`${config.name}.afrr_engagement_down`} label="AFRR Down" />
            <NumberInput source={`${config.name}.initialSocRestorationPower`} label="Initial SOC Restoration Power" />
            {config.chronicleFilename && (
              <TextInput source={`${config.name}.chronicleFilename`} label="Chronicle Filename" />
            )}
          </>
        )}

        {isOverlappingDays && (
          <Typography variant="caption" color="error">
            Attention: this planning overlaps two days!
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
